import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Slider,
  LinearProgress,
  Badge,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PlayArrow,
  Visibility,
  CloudUpload,
  Link as LinkIcon,
  CheckCircle,
  Cancel,
  Person,
  VideoLibrary,
  PendingActions,
} from '@mui/icons-material';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  getWatchVideos,
  createWatchVideo,
  updateWatchVideo,
  deleteWatchVideo,
  getVideoRequests,
  approveVideoRequest,
  declineVideoRequest,
  clearError,
} from '../store/slices/watchVideoSlice';
import { getProducts } from '../store/slices/productSlice';

const WatchVideos = () => {
  const dispatch = useDispatch();
  const {
    watchVideos = [],
    videoRequests = [],
    isLoading = false,
    error = null,
    message = null
  } = useSelector((state) => state.watchVideos || {});
  const { products = [] } = useSelector((state) => state.products || {});

  const [activeTab, setActiveTab] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoSourceType, setVideoSourceType] = useState('url'); // 'url' or 'file'
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoDimensions, setVideoDimensions] = useState({ width: 360, height: 240 });
  const videoRef = useRef(null);

  // Decline Dialog State
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    videoUrl: '',
    thumbnail: '',
    product: '',
    description: '',
    isActive: true,
    priority: 0,
  });

  useEffect(() => {
    // Fetch active videos and requests
    dispatch(getWatchVideos());
    dispatch(getVideoRequests('pending'));
    dispatch(getProducts({ limit: 1000 }));
  }, [dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      dispatch(getVideoRequests('pending'));
    } else {
      dispatch(getWatchVideos());
    }
  };

  const handleOpenDialog = (video = null) => {
    if (video) {
      setEditingVideo(video);
      setVideoSourceType(video.videoUrl?.startsWith('/uploads/videos/') ? 'file' : 'url');
      setFormData({
        title: video.title || '',
        videoUrl: video.videoUrl || '',
        thumbnail: video.thumbnail || '',
        product: video.product?._id || video.product || '',
        description: video.description || '',
        isActive: video.isActive !== undefined ? video.isActive : true,
        priority: video.priority || 0,
      });
      setVideoPreview(video.videoUrl?.startsWith('/uploads/videos/') ? video.videoUrl : null);
    } else {
      setEditingVideo(null);
      setVideoSourceType('url');
      setVideoFile(null);
      setVideoPreview(null);
      setFormData({
        title: '',
        videoUrl: '',
        thumbnail: '',
        product: '',
        description: '',
        isActive: true,
        priority: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVideo(null);
    setVideoFile(null);
    setVideoPreview(null);
    setUploading(false);
    setUploadProgress(0);
    dispatch(clearError());
  };

  const handleVideoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);

      const video = document.createElement('video');
      video.src = url;
      video.onloadedmetadata = () => {
        setVideoDimensions({
          width: video.videoWidth,
          height: video.videoHeight
        });
      };
    }
  };

  const uploadVideoFile = async () => {
    if (!videoFile) return null;
    const formData = new FormData();
    formData.append('video', videoFile);
    setUploading(true);
    setUploadProgress(0);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com';
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });
      setUploading(false);
      return response.data.videoUrl;
    } catch (error) {
      console.error('Video upload error:', error);
      setUploading(false);
      alert(error.response?.data?.message || 'Failed to upload video');
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!formData.title?.trim()) { alert('Please enter title'); return; }
    if (!formData.product) { alert('Please select product'); return; }

    let finalVideoUrl = formData.videoUrl;
    if (videoSourceType === 'file') {
      if (!videoFile && !formData.videoUrl) { alert('Please upload/enter video'); return; }
      if (videoFile) {
        const uploadedUrl = await uploadVideoFile();
        if (!uploadedUrl) return;
        finalVideoUrl = uploadedUrl;
      }
    }

    if (!finalVideoUrl?.trim()) { alert('Please provide video URL/File'); return; }

    const submitData = {
      title: formData.title.trim(),
      description: (formData.description || '').trim(),
      videoUrl: finalVideoUrl.trim(),
      thumbnail: (formData.thumbnail || '').trim(),
      product: typeof formData.product === 'object' ? formData.product._id : formData.product,
      isActive: formData.isActive,
      priority: parseInt(formData.priority) || 0,
    };

    try {
      if (editingVideo?._id) {
        await dispatch(updateWatchVideo({ id: editingVideo._id, watchVideoData: submitData })).unwrap();
        setSuccessMessage('Video updated successfully!');
      } else {
        await dispatch(createWatchVideo(submitData)).unwrap();
        setSuccessMessage('Video created successfully!');
      }
      handleCloseDialog();
      dispatch(getWatchVideos());
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving video:', err);
      alert(typeof err === 'string' ? err : err?.message || 'Failed to save video');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this video?')) {
      try {
        await dispatch(deleteWatchVideo(id)).unwrap();
        setSuccessMessage('Video deleted!');
        dispatch(getWatchVideos());
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        alert(err || 'Failed to delete');
      }
    }
  };

  // --- Approval Workflow Handlers ---
  const handleApprove = async (id) => {
    try {
      await dispatch(approveVideoRequest(id)).unwrap();
      setSuccessMessage('Video approved successfully!');
      // Refresh both lists
      dispatch(getVideoRequests('pending'));
      dispatch(getWatchVideos());
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert(err || 'Failed to approve video');
    }
  };

  const handleDeclineClick = (id) => {
    setSelectedRequestId(id);
    setDeclineReason('');
    setOpenDeclineDialog(true);
  };

  const handleDeclineSubmit = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    try {
      await dispatch(declineVideoRequest({ id: selectedRequestId, reason: declineReason })).unwrap();
      setSuccessMessage('Video rejected successfully');
      setOpenDeclineDialog(false);
      dispatch(getVideoRequests('pending'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert(err || 'Failed to reject video');
    }
  };

  const getVideoThumbnail = (videoUrl) => {
    if (videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be')) {
      const videoId = videoUrl.includes('youtu.be')
        ? videoUrl.split('youtu.be/')[1]?.split('?')[0]
        : videoUrl.split('v=')[1]?.split('&')[0];
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';
    }
    return '';
  };

  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const API_URL = process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com';
    const BASE_URL = API_URL.replace('/api', '');
    return `${BASE_URL}${path.startsWith('/') ? path : '/' + path}`;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" sx={{ fontWeight: 700, borderBottom: '2px solid #ff2852', pb: 1 }}>
          Watch And Buy Videos
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#ff2852', '&:hover': { bgcolor: '#5A189A' } }}
        >
          Add Video
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{
          '& .MuiTab-root': { fontWeight: 600 },
          '& .Mui-selected': { color: '#ff2852' },
          '& .MuiTabs-indicator': { backgroundColor: '#ff2852' }
        }}>
          <Tab icon={<VideoLibrary />} iconPosition="start" label="Active Videos" />
          <Tab
            icon={
              <Badge badgeContent={videoRequests?.length || 0} color="error">
                <PendingActions />
              </Badge>
            }
            iconPosition="start"
            label="Video Requests"
          />
        </Tabs>
      </Box>

      {(error || message) && (
        <Alert severity={error ? "error" : "success"} sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error || message}
        </Alert>
      )}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : activeTab === 0 ? (
        // --- Active Videos Grid ---
        <Grid container spacing={2}>
          {watchVideos.map((video) => (
            <Grid item xs={6} sm={4} md={3} lg={2.4} key={video._id}>
              <Card sx={{
                height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3,
                position: 'relative', bgcolor: '#000', aspectRatio: '9/16', overflow: 'hidden',
                transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <CardMedia
                  component="img"
                  image={video.thumbnail || getVideoThumbnail(video.videoUrl) || '/placeholder-video.jpg'}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }}
                />

                <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '50%', background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)' }} />

                <Chip
                  label={video.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    position: 'absolute', top: 10, right: 10,
                    bgcolor: video.isActive ? 'rgba(76, 175, 80, 0.9)' : 'rgba(158, 158, 158, 0.9)',
                    color: 'white', fontSize: '0.7rem', height: 24
                  }}
                />

                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', p: 1 }}>
                  <PlayArrow sx={{ color: 'white' }} />
                </Box>

                <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', p: 2, zIndex: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2, mb: 0.5, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                    {video.title.length > 40 ? video.title.substring(0, 40) + '...' : video.title}
                  </Typography>
                  {video.product && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mb: 1 }}>
                      {video.product.name}
                    </Typography>
                  )}
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }} onClick={() => handleOpenDialog(video)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#ff4d4d', bgcolor: 'rgba(255,0,0,0.1)' }} onClick={() => handleDelete(video._id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
          {watchVideos.length === 0 && <Typography sx={{ p: 4, width: '100%', textAlign: 'center' }}>No active videos found.</Typography>}
        </Grid>
      ) : (
        // --- Video Requests Grid ---
        <Grid container spacing={3}>
          {videoRequests.length === 0 ? (
            <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
              <CheckCircle sx={{ fontSize: 60, color: '#e0e0e0', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No pending video requests</Typography>
            </Box>
          ) : (
            videoRequests.map((req) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={req._id}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ position: 'relative', pt: '177%', bgcolor: '#000' }}>
                    <video
                      src={req.video?.startsWith('http') ? req.video : getFullUrl(req.video)}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      controls
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {req.name}
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Person fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" color="text.primary" fontWeight={500}>
                          {req.seller?.brandName || 'Unknown Seller'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {req.seller?.name || 'Seller'}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" gap={2} mt={2}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        startIcon={<CheckCircle />}
                        onClick={() => handleApprove(req._id)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<Cancel />}
                        onClick={() => handleDeclineClick(req._id)}
                      >
                        Decline
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingVideo ? 'Edit Watch Video' : 'Add Watch Video'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField label="Title *" fullWidth value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />

            <Tabs value={videoSourceType} onChange={(e, v) => setVideoSourceType(v)} sx={{ mb: 1 }}>
              <Tab label="YouTube URL" value="url" />
              <Tab label="Upload File" value="file" />
            </Tabs>

            {videoSourceType === 'url' ? (
              <TextField label="YouTube URL *" fullWidth value={formData.videoUrl} onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })} />
            ) : (
              <Box>
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUpload />}>
                  {videoFile ? videoFile.name : 'Choose Video File'}
                  <input type="file" hidden accept="video/*" onChange={handleVideoFileChange} />
                </Button>
                {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />}
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Product *</InputLabel>
              <Select value={formData.product} label="Product *" onChange={(e) => setFormData({ ...formData, product: e.target.value })}>
                {products.map((p) => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField label="Description" fullWidth multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            <TextField label="Thumbnail URL (Optional)" fullWidth value={formData.thumbnail} onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })} />
            <TextField label="Priority" type="number" fullWidth value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} />
            <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={uploading}>{uploading ? 'Uploading...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Decline Reason Dialog */}
      <Dialog open={openDeclineDialog} onClose={() => setOpenDeclineDialog(false)}>
        <DialogTitle>Decline Video Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please provide a reason for rejecting this video. The seller will be notified.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={4}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeclineDialog(false)}>Cancel</Button>
          <Button onClick={handleDeclineSubmit} variant="contained" color="error">Decline Request</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WatchVideos;
